import React from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import { VideoFilterToggleButton, CountSelect, PitchTypes } from 'yankees-react-utils';
import { useTranslation } from 'react-i18next';
import { Row } from 'react-bootstrap';

const StyledLabel = styled.label`
    margin-right: 10px;
`;
const StyledRow = styled(Row)`
    margin: 0;
`;
const AtBatFilters = (
    { filters, baseRunnerFilter, baseRunnerHideSomeOn, batterHandFilter, pitcherHandFilter,
        countsFilter, outsFilter, pitchTypeFilter, resultFilter, availablePitchTypes },
) => {
    const { t: translate } = useTranslation();

    const basesEmptyOptions = (baseRunnerHideSomeOn
        ? [{ name: translate('components.at-bat-filters.none-on'), value: 0, id: 'bases-0' }, { name: translate('baseball.abbr-runners-in-position'), value: 2, id: 'bases-2' }]
        : [{ name: translate('components.at-bat-filters.none-on'), value: 0, id: 'bases-0' }, { name: translate('components.at-bat-filters.runners-on'), value: 1, id: 'bases-1' }, { name: translate('baseball.abbr-runners-in-position'), value: 2, id: 'bases-2' }]);
    const lhpOptions = [{ name: translate('baseball.lhp'), value: 1, id: 'lhp-1' }, { name: translate('baseball.rhp'), value: 0, id: 'lhp-0' }];
    const lhbOptions = [{ name: translate('baseball.lhh'), value: 1, id: 'lhb-1' }, { name: translate('baseball.rhh'), value: 0, id: 'lhb-0' }];
    const hitResultOptions = [{ name: translate('baseball.pitchresult.abbr-groundball'), value: 0, id: 'pitch-result-0' },
        { name: translate('baseball.pitchresult.abbr-flyball'), value: 1, id: 'pitch-result-1' },
        { name: translate('baseball.pitchresult.abbr-linedrive'), value: 2, id: 'pitch-result-2' },
        { name: translate('baseball.pitchresult.abbr-popup'), value: 3, id: 'pitch-result-3' },
        { name: translate('baseball.pitchresult.abbr-bunt'), value: 4, id: 'pitch-result-4' }];

    function enforceAllAnswer(setter, newState) {
        if (newState.includes(-1) && newState.length > 1 && newState[newState.length - 1] !== -1) {
            newState.splice(newState.indexOf(-1), 1);
        }
        if (!newState || newState.length === 0 || (newState.length > 1 && newState[newState.length - 1] === -1)) {
            setter([-1]);
        } else {
            setter(newState);
        }
    }

    return (
        <>
            {typeof (baseRunnerFilter) !== 'undefined'
             && (
                 <>
                     <StyledRow>
                         <StyledLabel>{translate('baseball.runners_other')}:</StyledLabel>
                     </StyledRow>
                     <StyledRow>
                         <VideoFilterToggleButton
                             width="auto"
                             name="runners"
                             onChange={(value) => baseRunnerFilter(value)}
                             options={basesEmptyOptions(translate, baseRunnerHideSomeOn)}
                             type="radio"
                         />
                     </StyledRow>
                 </>
             )}
            {typeof (batterHandFilter) !== 'undefined'
             && (
                 <>
                     <StyledRow>
                         <StyledLabel>{translate('components.at-bat-filters.batter-hand')}:</StyledLabel>
                     </StyledRow>
                     <StyledRow>
                         <VideoFilterToggleButton
                             width="auto"
                             name="lhh"
                             onChange={(value) => batterHandFilter(value)}
                             options={lhbOptions(translate)}
                             type="radio"
                         />
                     </StyledRow>
                 </>
             )}
            {typeof (pitcherHandFilter) !== 'undefined'
             && (
                 <>
                     <StyledRow>
                         <StyledLabel>{translate('components.at-bat-filters.pitcher-hand')}:</StyledLabel>
                     </StyledRow>
                     <StyledRow>
                         <VideoFilterToggleButton
                             width="auto"
                             name="lhp"
                             onChange={(value) => pitcherHandFilter(value)}
                             options={lhpOptions(translate)}
                             type="radio"
                         />
                     </StyledRow>
                 </>
             )}
            {typeof (countsFilter) !== 'undefined'
             && (
                 <>
                     <StyledRow>
                         <StyledLabel>{translate('baseball.count')}</StyledLabel>
                     </StyledRow>
                     <StyledRow>
                         <CountSelect counts={filters?.counts} setCounts={(newVal) => countsFilter(newVal)} />
                     </StyledRow>
                 </>
             )}
            {typeof (outsFilter) !== 'undefined'
             && (
                 <>
                     <StyledRow>
                         <StyledLabel>{translate('baseball.outs_other')}:</StyledLabel>
                     </StyledRow>
                     <StyledRow>
                         <VideoFilterToggleButton
                             width="auto"
                             name="outs"
                             onChange={(value) => enforceAllAnswer(outsFilter, value)}
                             options={[0, 1, 2]}
                             filter={filters}
                         />
                     </StyledRow>
                 </>
             )}
            {typeof (pitchTypeFilter) !== 'undefined'
             && (
                 <>
                     <PitchTypes
                         availablePitchTypes={availablePitchTypes}
                         filter={{ pitchTypes: filters.pitchtype, showing: 0 }}
                         setFilter={(value) => enforceAllAnswer(pitchTypeFilter, value.pitchTypes)}
                     />
                 </>
             )}
            {typeof (resultFilter) !== 'undefined'
             && (
                 <>
                     <StyledRow>
                         <StyledLabel>{translate('common.result')}:</StyledLabel>
                     </StyledRow>
                     <StyledRow>
                         <VideoFilterToggleButton
                             width="auto"
                             name="pitchresult"
                             onChange={(value) => enforceAllAnswer(resultFilter, value)}
                             options={hitResultOptions(translate)}
                             filter={filters}
                             style={{ margin: '0' }}
                         />
                     </StyledRow>
                 </>
             )}
        </>
    );
};

AtBatFilters.propTypes = {
    filters: PropTypes.shape({
        outs: PropTypes.arrayOf(Number),
        pitchtype: PropTypes.arrayOf(Number),
        counts: PropTypes.arrayOf(String),
    }).isRequired,
    baseRunnerFilter: PropTypes.func,
    baseRunnerHideSomeOn: PropTypes.bool,
    batterHandFilter: PropTypes.func,
    pitcherHandFilter: PropTypes.func,
    countsFilter: PropTypes.func,
    outsFilter: PropTypes.func,
    pitchTypeFilter: PropTypes.func,
    resultFilter: PropTypes.func,
    availablePitchTypes: PropTypes.instanceOf(Set),
};

AtBatFilters.defaultProps = {
    baseRunnerFilter: undefined,
    baseRunnerHideSomeOn: false,
    batterHandFilter: undefined,
    pitcherHandFilter: undefined,
    countsFilter: undefined,
    outsFilter: undefined,
    pitchTypeFilter: undefined,
    resultFilter: undefined,
    availablePitchTypes: undefined,
};

export default AtBatFilters;
